import { useEffect, useReducer, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { listJobs } from '@/api/downloads'
import { getWsBaseUrl } from '@/api/client'
import { useAuth } from '@/auth/useAuth'
import { publishPlaybackSignal } from '@/player/sync'
import type { JobEvent, JobResponse, JobStatus, WsEvent } from '@/api/types'

export interface JobView {
  jobId: string
  status: JobStatus
  url: string
  // Человекочитаемый заголовок. Может отсутствовать у оптимистичной задачи.
  title?: string
  progress: number
  speedBytesPerSec?: number
  etaSeconds?: number
  error?: string | null
  episodeId?: string | null
  createdAt?: string
}

interface State {
  byId: Map<string, JobView>
  connected: boolean
}

type Action =
  | { type: 'set-from-snapshot'; jobs: JobView[] }
  | { type: 'set-from-rest'; jobs: JobResponse[] }
  | { type: 'apply-event'; event: JobEvent }
  | { type: 'add-optimistic'; job: JobView }
  | { type: 'remove'; jobId: string }
  | { type: 'connection'; connected: boolean }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'set-from-snapshot': {
      // Снапшот приходит только с активными задачами (бэкенд фильтрует его по
      // _ACTIVE_STATUSES в services/handlers/ws.py) — значит он авторитетен только
      // для активных: локальные завершённые (done/error) сохраняем, иначе они
      // пропадали бы с дашборда на каждом (пере)подключении WS. Полный список даёт
      // REST — он же и вычистит задачу, удалённую с другого устройства.
      // Второе исключение — локальные оптимистичные queued-задачи, которые гонкой
      // опередили broadcast от сервера.
      const ids = new Set(action.jobs.map((j) => j.jobId))
      const byId = preserveLocal(state.byId, ids, { keepTerminal: true })
      for (const j of action.jobs) {
        const existing = state.byId.get(j.jobId)
        byId.set(j.jobId, existing ? { ...existing, ...j } : j)
      }
      return { ...state, byId }
    }
    case 'set-from-rest': {
      // REST-список полный (все статусы) и авторитетен: чего в нём нет — исчезло.
      const ids = new Set(action.jobs.map((j) => j.jobId))
      const byId = preserveLocal(state.byId, ids, { keepTerminal: false })
      for (const j of action.jobs) {
        const existing = state.byId.get(j.jobId)
        byId.set(j.jobId, {
          ...existing,
          jobId: j.jobId,
          status: j.status,
          url: j.url,
          title: j.title,
          progress: j.progress,
          error: j.error,
          episodeId: j.episodeId,
          createdAt: j.createdAt,
        })
      }
      return { ...state, byId }
    }
    case 'apply-event': {
      const e = action.event
      const byId = new Map(state.byId)
      const existing = byId.get(e.jobId) ?? {
        jobId: e.jobId,
        status: 'queued' as JobStatus,
        url: '',
        progress: 0,
      }
      switch (e.type) {
        case 'queued':
          byId.set(e.jobId, {
            ...existing,
            status: 'queued',
            progress: 0,
            speedBytesPerSec: undefined,
            etaSeconds: undefined,
          })
          break
        case 'downloading':
          byId.set(e.jobId, {
            ...existing,
            status: 'downloading',
            progress: e.progress,
            // title в downloading может быть null, пока не зарезолвлен — не затираем сохранённый.
            title: e.title ?? existing.title,
            speedBytesPerSec: e.speedBytesPerSec,
            etaSeconds: e.etaSeconds,
          })
          break
        case 'transcoding':
          // Скорость/ETA с downloading не имеют смысла на следующей стадии — чистим.
          byId.set(e.jobId, {
            ...existing,
            status: 'transcoding',
            progress: e.progress,
            speedBytesPerSec: undefined,
            etaSeconds: undefined,
          })
          break
        case 'fetching':
          // Забор готового файла с ноды к оркестратору — speed/eta тут не приходят, чистим.
          byId.set(e.jobId, {
            ...existing,
            status: 'fetching',
            progress: e.progress,
            speedBytesPerSec: undefined,
            etaSeconds: undefined,
          })
          break
        case 'uploading':
          byId.set(e.jobId, {
            ...existing,
            status: 'uploading',
            progress: e.progress,
            speedBytesPerSec: undefined,
            etaSeconds: undefined,
          })
          break
        case 'done':
          byId.set(e.jobId, {
            ...existing,
            status: 'done',
            progress: 1,
            episodeId: e.episodeId,
            speedBytesPerSec: undefined,
            etaSeconds: undefined,
          })
          break
        case 'error':
          byId.set(e.jobId, {
            ...existing,
            status: 'error',
            error: e.reason,
            speedBytesPerSec: undefined,
            etaSeconds: undefined,
          })
          break
        case 'deleted':
          byId.delete(e.jobId)
          break
      }
      return { ...state, byId }
    }
    case 'add-optimistic': {
      const byId = new Map(state.byId)
      byId.set(action.job.jobId, action.job)
      return { ...state, byId }
    }
    case 'remove': {
      const byId = new Map(state.byId)
      byId.delete(action.jobId)
      return { ...state, byId }
    }
    case 'connection':
      return { ...state, connected: action.connected }
  }
}

// Переносит в новый byId локальные задачи, которых нет в серверном списке:
// - оптимистичные queued — могли опередить серверный snapshot/REST (broadcast ещё
//   в пути), иначе только что созданная задача мигала бы и пропадала;
// - завершённые (done/error) при keepTerminal — их не бывает в WS-снапшоте by design,
//   их отсутствие там не означает, что задача исчезла.
function preserveLocal(
  prev: Map<string, JobView>,
  serverIds: Set<string>,
  { keepTerminal }: { keepTerminal: boolean },
): Map<string, JobView> {
  const byId = new Map<string, JobView>()
  for (const [id, j] of prev) {
    if (serverIds.has(id)) continue
    const terminal = j.status === 'done' || j.status === 'error'
    if (j.status === 'queued' || (keepTerminal && terminal)) byId.set(id, j)
  }
  return byId
}

function countActive(byId: Map<string, JobView>): number {
  let n = 0
  for (const j of byId.values()) {
    if (j.status !== 'done' && j.status !== 'error') n++
  }
  return n
}

const RECONNECT_BASE_MS = 1000
const RECONNECT_MAX_MS = 30_000
// Если сокет молчит дольше — при наличии активных задач считаем его «тихо мёртвым»
// (TCP мог отвалиться без onclose, readyState всё ещё OPEN) и форсим реконнект.
const IDLE_TIMEOUT_MS = 40_000

// Экспонента с насыщением + equal jitter: половина задержки фиксированная, половина
// случайная — это гасит «стадо» одновременных реконнектов от множества вкладок/устройств.
function backoffDelay(attempt: number, random: number): number {
  const exp = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** (attempt - 1))
  return Math.round(exp / 2 + random * (exp / 2))
}

const INITIAL: State = { byId: new Map(), connected: false }

export interface UseJobsStreamApi {
  jobs: JobView[]
  connected: boolean
  addOptimistic: (job: JobView) => void
  remove: (jobId: string) => void
}

export function useJobsStream(): UseJobsStreamApi {
  const queryClient = useQueryClient()
  const { getAccessToken } = useAuth()
  const [state, dispatch] = useReducer(reducer, INITIAL)

  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<number | null>(null)
  const idleTimerRef = useRef<number | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const connectingRef = useRef(false)
  // Зеркало числа незавершённых задач — читается idle-watchdog'ом без перезапуска
  // эффекта соединения (иначе WS пересоздавался бы на каждый тик прогресса).
  const activeRef = useRef(0)
  useEffect(() => {
    activeRef.current = countActive(state.byId)
  }, [state.byId])

  useEffect(() => {
    let cancelled = false

    function clearReconnectTimer() {
      if (reconnectTimerRef.current !== null) {
        window.clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }

    function clearIdleTimer() {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
    }

    function armIdleTimer() {
      clearIdleTimer()
      idleTimerRef.current = window.setTimeout(() => {
        if (cancelled) return
        // Активные задачи обязаны слать прогресс. Тишина дольше таймаута =
        // соединение «тихо умерло» → форсим реконнект. В простое сервер вправе
        // молчать — просто продолжаем следить, не дёргая реконнект зря.
        if (activeRef.current > 0) forceReconnect()
        else armIdleTimer()
      }, IDLE_TIMEOUT_MS)
    }

    function closeSocket() {
      const ws = wsRef.current
      if (ws) {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null
        try {
          ws.close()
        } catch {
          // noop
        }
        wsRef.current = null
      }
    }

    async function fetchRestSnapshot() {
      try {
        const jobs = await listJobs()
        if (!cancelled) dispatch({ type: 'set-from-rest', jobs })
      } catch {
        // ignore — likely auth issue, AuthProvider handle it
      }
    }

    async function connect() {
      if (cancelled || connectingRef.current) return
      connectingRef.current = true
      try {
        // Гарантия единственного сокета: глушим предыдущий перед новым.
        closeSocket()
        // REST вызов работает и для дашборда, и форсит refresh access-токена
        // через общий API-клиент (там reuse-safe inflight-дедуп).
        await fetchRestSnapshot()
        if (cancelled) return
        const access = getAccessToken()
        if (!access) {
          scheduleReconnect()
          return
        }

        const url = `${getWsBaseUrl()}/v0/ws?token=${encodeURIComponent(access)}`
        const ws = new WebSocket(url)
        wsRef.current = ws

        ws.onopen = () => {
          if (cancelled) return
          reconnectAttemptsRef.current = 0
          dispatch({ type: 'connection', connected: true })
          armIdleTimer()
        }
        ws.onmessage = (event) => {
          // Любое сообщение — признак живого соединения: перевзводим watchdog.
          armIdleTimer()
          try {
            const data = JSON.parse(event.data as string) as WsEvent
            if (data.type === 'playback') {
              // Не задача — в jobs-редьюсер не идёт. Плеер подписан на мост и сам
              // отсеивает эхо, патчит кэш эпизодов и синхронизирует позицию.
              publishPlaybackSignal({ type: 'event', event: data })
            } else if (data.type === 'snapshot') {
              dispatch({
                type: 'set-from-snapshot',
                jobs: data.jobs.map((j) => ({
                  jobId: j.jobId,
                  status: j.status,
                  url: j.url,
                  title: j.title,
                  progress: j.progress,
                })),
              })
              // Snapshot приходит на каждое (пере)подключение: playback-события за время
              // разрыва могли потеряться (core-NATS lossy) — плеер перечитает позицию.
              publishPlaybackSignal({ type: 'resync' })
            } else {
              dispatch({ type: 'apply-event', event: data })
              if (data.type === 'done') {
                void queryClient.invalidateQueries({ queryKey: ['episodes'] })
                void queryClient.invalidateQueries({ queryKey: ['quota'] })
              }
            }
          } catch {
            // ignore malformed event
          }
        }
        ws.onerror = () => {
          dispatch({ type: 'connection', connected: false })
        }
        ws.onclose = () => {
          if (cancelled) return
          wsRef.current = null
          clearIdleTimer()
          dispatch({ type: 'connection', connected: false })
          // Бэкенд бродкастит во все соединения (podpipe-api#13) — больше нет
          // «superseded»-закрытий. Любой разрыв = переподключаемся с backoff.
          scheduleReconnect()
        }
      } finally {
        connectingRef.current = false
      }
    }

    function scheduleReconnect() {
      if (cancelled) return
      clearReconnectTimer()
      const attempt = ++reconnectAttemptsRef.current
      reconnectTimerRef.current = window.setTimeout(
        () => {
          void connect()
        },
        backoffDelay(attempt, Math.random()),
      )
    }

    function forceReconnect() {
      if (cancelled) return
      clearIdleTimer()
      closeSocket()
      dispatch({ type: 'connection', connected: false })
      reconnectAttemptsRef.current = 0
      clearReconnectTimer()
      void connect()
    }

    // Сетевые события окна: реконнект быстрее, чем по backoff (сон ноутбука,
    // сетевые блипы). Если сокет уже жив/подключается — ничего не делаем.
    function maybeReconnect() {
      if (cancelled) return
      const ws = wsRef.current
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) return
      reconnectAttemptsRef.current = 0
      clearReconnectTimer()
      void connect()
    }
    function onOnline() {
      maybeReconnect()
    }
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') maybeReconnect()
    }
    window.addEventListener('online', onOnline)
    document.addEventListener('visibilitychange', onVisibilityChange)

    void connect()

    // REST polling-фолбэк: пока WS не открыт, обновляем дашборд каждые 5 секунд.
    const pollTimer = window.setInterval(() => {
      const ws = wsRef.current
      if (ws && ws.readyState === WebSocket.OPEN) return
      void fetchRestSnapshot()
    }, 5000)

    return () => {
      cancelled = true
      clearReconnectTimer()
      clearIdleTimer()
      window.clearInterval(pollTimer)
      window.removeEventListener('online', onOnline)
      document.removeEventListener('visibilitychange', onVisibilityChange)
      closeSocket()
    }
  }, [queryClient, getAccessToken])

  return {
    jobs: sortJobs(state.byId),
    connected: state.connected,
    addOptimistic: (job) => dispatch({ type: 'add-optimistic', job }),
    remove: (jobId) => dispatch({ type: 'remove', jobId }),
  }
}

const STATUS_ORDER: Record<JobStatus, number> = {
  downloading: 0,
  transcoding: 1,
  fetching: 2,
  uploading: 3,
  queued: 4,
  error: 5,
  done: 6,
}

function sortJobs(byId: Map<string, JobView>): JobView[] {
  return Array.from(byId.values()).sort((a, b) => {
    const byStatus = STATUS_ORDER[a.status] - STATUS_ORDER[b.status]
    if (byStatus !== 0) return byStatus
    if (a.createdAt && b.createdAt) return a.createdAt > b.createdAt ? -1 : 1
    return 0
  })
}
